import { ChevronDownIcon } from '@heroicons/react/24/outline'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/utils'

export type DropdownOption = {
  title: string
  description?: string
  Icon: React.ComponentType<{ className?: string }>
  handler: (e: React.MouseEvent) => void
  disabled?: boolean
}

type ToolbarSplitButtonProps = {
  Icon: React.ComponentType<{ className?: string }>
  defaultAction: (e: React.MouseEvent) => void
  defaultOption?: DropdownOption
  dropdownOptions?: DropdownOption[]
  tooltip?: string
  isDisabled?: boolean
}

export const ToolbarSplitButton = ({
  Icon,
  defaultAction,
  defaultOption,
  dropdownOptions = [],
  tooltip,
  isDisabled = false,
}: ToolbarSplitButtonProps) => {
  const allOptions = defaultOption
    ? [defaultOption, ...dropdownOptions]
    : dropdownOptions

  return (
    <div className="flex h-full py-0 px-2">
      {/* Left side - Default action button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                'rounded-r-none hover:bg-gray-100 m-0 h-full flex py-2 px-1 items-center justify-center disabled:opacity-20',
              )}
              onClick={defaultAction}
              disabled={isDisabled}
            >
              <Icon className="h-[20px] w-[20px] stroke-2" />
            </Button>
          </TooltipTrigger>
          {tooltip && (
            <TooltipContent className="bg-black text-white">
              <p>{tooltip}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>

      {/* Right side - Dropdown menu */}
      {allOptions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            disabled={isDisabled}
            className={cn(
              'rounded-l-none rounded-md hover:bg-gray-100 m-0 flex items-center justify-center h-full px-0.5',
              {
                'opacity-25': isDisabled,
              },
            )}
          >
            <ChevronDownIcon className="h-[18px] w-[18px] stroke-2" />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="flex min-w-0 px-1.5 py-2">
            <ul className="w-full">
              {allOptions.map((option, i) => (
                <li
                  key={`option-${i}`}
                  className="flex flex-row items-center justify-between mb-2 last:mb-0 border-b last:border-b-0 border-gray-100"
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuItem asChild disabled={option.disabled}>
                          <Button
                            variant="outline"
                            className="flex flex-row items-center space-x-2 hover:bg-gray-100 hover:border-0 border-0 shadow-none w-full justify-start text-left h-full p-2 cursor-pointer"
                            onClick={option.handler}
                            disabled={option.disabled}
                          >
                            <option.Icon className="h-7 w-7 text-blue-500" />
                            <div>
                              <p className="text-sm font-medium">
                                {option.title}
                              </p>
                              {option.description && (
                                <p className="text-xs text-gray-500">
                                  {option.description}
                                </p>
                              )}
                            </div>
                          </Button>
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      <TooltipContent className="bg-black text-white">
                        <p>{option.title}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </li>
              ))}
            </ul>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
